import { generateGuid } from 'guid';
import { angularInjectorProvider } from 'Injector';
import { EventLoggerProvider } from 'EventLogger';
import { type ReactRouterBridge } from 'Navigation/angularModule/scripts/react_router_bridge';
import { type AnyObject } from '@Types';
import { type SearchState } from './BrowseCourses.types';

/*
    A SearchAttempt is one attempt to find courses on the Courses page. It exists only for logging.

    A new attempt starts when the user selects a topic or enters search text. The attempt ends when the
    user removes a topic or deletes part of the search text, because that suggests the search failed. It
    also ends on the next route change, unless that route is the dashboard of a course in the results.
    A dashboard visit is recorded and the attempt stays open, to see if the user then launches a lesson.
*/

// The one open attempt. A new attempt starts only when none is open.
let currentSearchAttempt: SearchAttempt | undefined;
let listeningForRouteChanges = false;

type AttemptFilters = { topicsToFilterBy: string[]; progressOptionsToFilterBy: string[]; searchText: string };

class SearchAttempt {
    readonly id = generateGuid();

    filters!: AttemptFilters;

    resultStreamIds: string[] = [];

    viewedStreamDashboardId?: string;

    launchedLessonId?: string;

    launchedStreamId?: string;

    destroyed = false;

    constructor(state: SearchState) {
        this.filtersAdded(state, false);
        this.log('library_search:start');
    }

    // Progress filters are ignored on purpose: switching from completed to not started is not a failed
    // search.
    private destroyIfChanged(state: SearchState) {
        const topicRemoved = this.filters.topicsToFilterBy.some(topic => !state.topics.includes(topic));
        const searchTextDeleted = !!this.filters.searchText && !state.searchText.match(this.filters.searchText);

        if (topicRemoved || searchTextDeleted) {
            this.destroy();
            return true;
        }
        return false;
    }

    filtersAdded(state: SearchState, logUpdate: boolean) {
        if (this.filters && this.destroyIfChanged(state)) return;

        this.filters = {
            topicsToFilterBy: [...state.topics],
            progressOptionsToFilterBy: [...state.progressIds],
            searchText: state.searchText,
        };
        this.resultStreamIds = [...state.resultStreamIds];

        if (logUpdate) this.log('library_search:update');
    }

    routeChange(reactRouterBridge: ReactRouterBridge) {
        const pathname = reactRouterBridge.path();
        const { routeName } = reactRouterBridge;
        const params = reactRouterBridge.params();
        const streamId = params.stream_id ?? params.streamId;
        const lessonId = params.lesson_id ?? params.lessonId;
        const routeIsForResultStream = !!streamId && this.resultStreamIds.includes(streamId);

        const isCourseRoute = routeName === 'course';
        const isStreamRoute = routeName === 'stream';
        const isShowStreamRoute = (isCourseRoute || isStreamRoute) && pathname.includes('/lesson/');
        const isStreamDashboardRoute =
            isCourseRoute && !pathname.includes('/lesson/') && !/(?:\/completed)\/?$/.test(pathname);

        if (isStreamDashboardRoute && routeIsForResultStream) {
            this.viewedStreamDashboardId = streamId;
            return;
        }

        if (isShowStreamRoute && routeIsForResultStream) {
            this.launchedLessonId = lessonId;
            this.launchedStreamId = streamId;
        }
        this.destroy();
    }

    destroy() {
        if (currentSearchAttempt === this) currentSearchAttempt = undefined;
        if (!this.destroyed) {
            this.log('library_search:finish', {
                aborted: !this.viewedStreamDashboardId && !this.launchedLessonId,
                launched_lesson_id: this.launchedLessonId,
                viewed_stream_dashboard_id: this.viewedStreamDashboardId,
                launched_stream_id: this.launchedStreamId,
            });
        }
        this.destroyed = true;
    }

    private log(eventType: string, extra: AnyObject = {}) {
        EventLoggerProvider.get()?.log(eventType, {
            search_attempt_id: this.id,
            topics_to_filter_by: this.filters.topicsToFilterBy,
            progress_options_to_filter_by: this.filters.progressOptionsToFilterBy,
            search_text: this.filters.searchText,
            result_stream_ids: this.resultStreamIds,
            has_some_results: this.resultStreamIds.length > 0,
            ...extra,
        });
    }
}

function listenForRouteChanges() {
    if (listeningForRouteChanges) return;
    const reactRouterBridge = angularInjectorProvider.get<ReactRouterBridge>('reactRouterBridge');
    reactRouterBridge.on('routeChangeSuccess', () => currentSearchAttempt?.routeChange(reactRouterBridge));
    listeningForRouteChanges = true;
}

// Call each time the results change.
export function searchFiltersUpdated(state: SearchState) {
    listenForRouteChanges();

    currentSearchAttempt?.filtersAdded(state, true);

    const hasFilters = state.topics.length > 0 || !!state.searchText;
    if (hasFilters && !currentSearchAttempt) currentSearchAttempt = new SearchAttempt(state);
}

// Specs only
export function resetSearchAttemptsForSpecs() {
    currentSearchAttempt = undefined;
    listeningForRouteChanges = false;
}
